import { useContext, useMemo, useState } from 'react';
import { FoldersNavDrawer, TJurisdictionMap } from '@modules/items';
import { AccessType } from '@modules/access';
import { ControlledTooltip, JurisdictionFilter, ListPageLayout, Path } from '@shared/components';
import { useFolderPathAsBreadcrumbs } from '@shared/hooks';

import { TAllSubmissionsData, TCreateSubmissionDialogState } from '../../types/submissions.types';
import { ARCHIVED_JURISDICTION_VIRTUAL_ID, FULL_PATH_SUBMISSIONS_ITEM } from '../../constants';
import { Button } from '@fluentui/react-components';
import { AddFilled } from '@fluentui/react-icons';
import SubmissionsTable from './SubmissionsTable';
import CreateSubmissionDialog from '../single/dialogs/CreateSubmissionDialog';
import { SignInContext, USER_GROUPS } from '@modules/users';

const AllSubmissionsContent = ({ product, submissions }: TAllSubmissionsData) => {
  const { user } = useContext(SignInContext);
  const productId = product._id;

  // Rebuild the "fullPath" for the FoldersNavDrawer
  const fullPath = [
    { _id: productId, name: product.name, accessRight: product.accessRight },
    FULL_PATH_SUBMISSIONS_ITEM,
  ];

  // find submissions with archived jurisdiction:
  const archivedJurisdictionIds = submissions
    .filter((submission) => !product.jurisdictions.some((jurisdiction) => jurisdiction._id === submission.jurisdiction))
    .map((submission) => submission.jurisdiction);

  // Selected jurisdictions (IDs)
  const [selectedJurisdictionIds, setSelectedJurisdictionIds] = useState<string[]>(
    // if there are submissions with archived jurisdictions, add the virtual Archived selection-option:
    [
      ...product.jurisdictions.map((j) => j._id),
      ...(archivedJurisdictionIds.length ? [ARCHIVED_JURISDICTION_VIRTUAL_ID] : []),
    ]
  );

  // Use a single state object for the "Create Submission" dialog.
  const [createDialogState, setCreateDialogState] = useState<TCreateSubmissionDialogState>({
    isOpen: false,
    productId,
    productName: product.name,
    jurisdictions: product.jurisdictions,
  });

  // Handler to open the dialog – we update the state object.
  const handleNewSubmission = () => {
    setCreateDialogState((prev) => ({ ...prev, isOpen: true }));
  };

  const jurisdictionsMap = useMemo(() => {
    const map: TJurisdictionMap = {};
    product.jurisdictions.forEach((jurisdiction) => {
      map[jurisdiction._id] = jurisdiction.name;
    });
    return map;
  }, [product.jurisdictions]);

  // Filter submissions based on currently selected jurisdictions.
  const filteredSubmissions = useMemo(
    () =>
      submissions.filter((s) =>
        archivedJurisdictionIds.includes(s.jurisdiction)
          ? selectedJurisdictionIds.includes(ARCHIVED_JURISDICTION_VIRTUAL_ID)
          : selectedJurisdictionIds.includes(s.jurisdiction)
      ),
    [submissions, archivedJurisdictionIds, selectedJurisdictionIds]
  );

  const hasCreatePermission = product.accessRight >= AccessType.MANAGE || user.groups.includes(USER_GROUPS.ADMIN);

  return (
    <ListPageLayout nav={<FoldersNavDrawer fullPath={fullPath} selectedValue="submissions" folder={product} />}>
      <div className="flex flex-col space-y-5 pt-4 pb-16 pl-5 pr-8">
        <Path items={useFolderPathAsBreadcrumbs(fullPath)} hasCurrent size="large" />
        <ControlledTooltip
          content="You do not have permission to create new submissions."
          relationship="inaccessible"
          enable={!hasCreatePermission}
        >
          <Button
            appearance="primary"
            size="medium"
            disabled={!hasCreatePermission}
            icon={<AddFilled />}
            onClick={handleNewSubmission}
            className="w-fit"
          >
            New
          </Button>
        </ControlledTooltip>
        <div className="max-w-screen-sm">
          <JurisdictionFilter
            label="Filter submissions by jurisdiction:"
            folder={product}
            withArchived={archivedJurisdictionIds.length > 0}
            selectedFolderIds={selectedJurisdictionIds}
            onChangeSelectedFolderIds={setSelectedJurisdictionIds}
          />
        </div>
        {filteredSubmissions.length ? (
          <SubmissionsTable submissions={filteredSubmissions} jurisdictionsMap={jurisdictionsMap} />
        ) : (
          <p>No submissions found.</p>
        )}
      </div>

      <CreateSubmissionDialog state={createDialogState} setState={setCreateDialogState} />
    </ListPageLayout>
  );
};

export default AllSubmissionsContent;
